import { TEvent } from '@ws/shared/types';

import { getChannel } from '../../redux/middlewares/sagaChannel';
import { syncHealth } from '../../redux/sync/sync.actions';
import { getResourceManager } from '../../resources';
import { iDBActService } from '../../resources/iDBAct';
import { EventQueue } from '../EventTrain/EventQueue';
import { EventTrain } from '../EventTrain/EventTrain';
import { SyncState } from '../EventTrain/SyncState';
import { LastUpdateIdKeeper } from '../EventTrain/lastUpdateIdKeeper';
import { syncStateValidator } from '../projectProcessing/SyncStateValidator';

import { SyncSession } from './SyncSession';
import { TSyncSessionToken, TSyncStateInfo } from './session.types';

// Единственный владелец жизненного цикла синхронизации (в пределах одной вкладки).
// Ровно четыре операции над сессиями: beginSession / peekActive / quiesce / endSession.
// Чтение записи — read-only (findSyncState, НЕ создаёт), создание — явное
// (createSyncState). Гонка «двух вызывающих» исключена: сессии открывает только он.
class SyncSessionManager {
  private _active: SyncSession | null = null;
  private _generation = 0;
  // Закрытие последней сессии каждого projectId. Новая сессия того же проекта
  // гидратируется ТОЛЬКО после того, как прежняя полностью settled+finalized —
  // иначе она прочитала бы ещё не подтверждённую очередь и повторно отправила уже
  // принятый сервером event (C1/C6).
  private _closingByProject = new Map<string, Promise<void>>();

  // Синхронно закрывает предыдущую сессию (пометка на закрытие + асинхронная
  // финализация) и открывает новую. Гидратация IDB — асинхронно внутри session.ready.
  // Вызывать ТОЛЬКО из saga на PROJECT_SET, синхронно с ним.
  public beginSession(projectId: string): SyncSession {
    const prev = this._active;
    if (prev) {
      // Синхронно: старый train (если уже гидратирован) немедленно уходит в тишину —
      // между сменой проектов не проскочит новая отправка.
      prev.markClosing();
      // Асинхронно: дождаться in-flight apply (durable ack) и финализировать.
      // Запоминаем промис закрытия по projectId, чтобы новая сессия того же проекта
      // дождалась его перед гидратацией.
      const closing = this.endSession(prev).catch((error) => {
        console.error('endSession failed', error);
      });
      this._closingByProject.set(prev.token.projectId, closing);
    }

    this._generation += 1;
    const token: TSyncSessionToken = { projectId, generation: this._generation };
    let session: SyncSession;
    // eslint-disable-next-line prefer-const
    session = new SyncSession(token, () => this._active === session);
    this._active = session;

    void this.hydrate(projectId, token, session).catch((error) => {
      console.error('hydrate failed', error);
      session.applyHydration({ kind: 'storage-error', error }, null);
    });

    return session;
  }

  public peekActive(): SyncSession | null {
    return this._active;
  }

  // Тишина: новые отправки/циклы стоп, in-flight apply завершается durable.
  public async quiesce(session: SyncSession): Promise<void> {
    await session.ready;
    const { train } = session;
    if (!train) {
      return;
    }
    train.quiesceMode();
    await train.settleInFlight();
  }

  public async endSession(session: SyncSession): Promise<void> {
    // markClosing синхронно (до первого await): даже вызванный из PROJECT_CLEAN,
    // endSession немедленно снимает session с «текущих» (isCurrent()→false), пока
    // ждёт гидратацию/in-flight — иначе поздний worker снова покрасил бы UI (W3).
    session.markClosing();
    await this.quiesce(session);
    session.train?.finalize();
    if (this._active === session) {
      this._active = null;
    }
  }

  // Завершить активную сессию (PROJECT_CLEAN): старый projectId перестаёт быть валидным.
  public async endActiveSession(): Promise<void> {
    const active = this._active;
    if (active) {
      await this.endSession(active);
    }
  }

  // Read-only: НЕ создаёт запись при отсутствии (в отличие от старого getSyncState).
  public async findSyncState(projectId: string): Promise<TSyncStateInfo> {
    try {
      const saved = await iDBActService.getOneById(projectId);

      if (!saved) {
        return { kind: 'missing' };
      }

      const { isOk, value } = syncStateValidator.validate(saved);
      if (!isOk || !value) {
        return { kind: 'invalid' };
      }

      const state = new SyncState<TEvent>({
        isSyncEnabled: value.isSyncEnabled,
        projectId: value.id,
        lastUpdateIdKeeper: new LastUpdateIdKeeper(value.lastUpdateId),
        eventQueue: new EventQueue<TEvent>(value.list),
      });

      return { kind: value.isSyncEnabled ? 'enabled' : 'disabled', state };
    } catch (error) {
      return { kind: 'storage-error', error };
    }
  }

  // Явное создание+персист записи. Вызывается только флоу включения синка/создания
  // проекта — не путём чтения.
  public async createSyncState(
    projectId: string,
    {
      isSyncEnabled = true,
      lastUpdateId = null,
    }: { isSyncEnabled?: boolean; lastUpdateId?: string | null } = {},
  ): Promise<SyncState<TEvent>> {
    const state = new SyncState<TEvent>({
      isSyncEnabled,
      projectId,
      lastUpdateIdKeeper: new LastUpdateIdKeeper(lastUpdateId),
      eventQueue: new EventQueue<TEvent>([]),
    });

    await iDBActService.saveOne(state.toStorage());

    return state;
  }

  private async hydrate(
    projectId: string,
    token: TSyncSessionToken,
    session: SyncSession,
  ): Promise<void> {
    // Дождаться полного закрытия прежней сессии этого же проекта (settle + finalize +
    // durable-персист), прежде чем читать IDB — иначе прочитали бы не подтверждённую
    // очередь (C1/C6).
    const priorClose = this._closingByProject.get(projectId);
    if (priorClose) {
      await priorClose;
      if (this._closingByProject.get(projectId) === priorClose) {
        this._closingByProject.delete(projectId);
      }
    }

    const info = await this.findSyncState(projectId);

    let train: EventTrain<TEvent> | null = null;
    if (info.kind === 'enabled' || info.kind === 'disabled') {
      train = this.buildTrain(projectId, info.state, token);
    }

    session.applyHydration(info, train);
  }

  private buildTrain(
    projectId: string,
    syncState: SyncState<TEvent>,
    token: TSyncSessionToken,
  ): EventTrain<TEvent> {
    // Единый durable-персист состояния в IDB для всех трёх хуков (charge/apply/delta).
    const persist = (tempState: SyncState<TEvent>) => iDBActService.saveOne(tempState.toStorage());

    return new EventTrain<TEvent>({
      isBlocked: true,
      isOffline: !navigator.onLine,
      isNotVisible: document.hidden,
      syncState,
      sessionToken: token,
      onCharge: persist,
      onApplySuccess: persist,
      onSyncStateChange: persist,
      fetcherFunc: async (event: TEvent, lastUpdateId: string) => {
        const manager = getResourceManager();
        return await manager.SyncService.applyEvent({ projectId, event, lastUpdateId });
      },
      deltaFunc: async (lastUpdateId: string) => {
        const manager = getResourceManager();
        return await manager.SyncService.getDelta({ projectId, lastUpdateId });
      },
      communicate: async (action) => {
        const channel = getChannel();
        channel.put(action);
      },
      onHealth: (event) => {
        const channel = getChannel();
        channel.put(syncHealth(event));
      },
    });
  }
}

const syncSessionManager = new SyncSessionManager();

export const beginSession = (projectId: string) => syncSessionManager.beginSession(projectId);
export const peekActiveSession = () => syncSessionManager.peekActive();
export const endActiveSession = () => syncSessionManager.endActiveSession();
export const findSyncState = (projectId: string) => syncSessionManager.findSyncState(projectId);
export const createSyncState = (
  projectId: string,
  options?: { isSyncEnabled?: boolean; lastUpdateId?: string | null },
) => syncSessionManager.createSyncState(projectId, options);
